/**
 * PageHero — Standardized full-width page header for data-heavy
 * dashboards. Modeled on the existing Saved Scenarios header so
 * the rest of the app can converge to a single visual vocabulary.
 *
 * Anatomy:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ [icon] TITLE                          [📌 pin] [actions ▶] │ ← top row
 *   │ description                                                 │
 *   │                                                             │
 *   │ ┌──────────┐ ┌──────────┐ ┌──────────┐                     │ ← optional stats
 *   │ │ stat 1   │ │ stat 2   │ │ stat 3   │                     │
 *   │ └──────────┘ └──────────┘ └──────────┘                     │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Props:
 *   - title:        string (required)
 *   - description:  string (recommended)
 *   - theme:        section theme object from `lib/sectionThemes`
 *   - icon:         lucide-react icon component (optional)
 *   - actions:      ReactNode rendered top-right (optional)
 *   - stats:        Array of {label, value, hint?, testId?} OR a
 *                   ReactNode for fully-custom stat row.
 *   - northStar:    Optional CFO-grade centerpiece tile rendered
 *                   between description and stats. Schema:
 *                   { label, value, valueSuffix?, valueTone?,
 *                     description?, percentile?, percentileLabel?,
 *                     delta?, deltaText?, deltaLabel?,
 *                     deltaInverted?, sparklineData?, peerLabel?,
 *                     peerValue?, testId? }.
 *                   When omitted, the hero falls through to the
 *                   regular stats grid (back-compat).
 *   - narrative:    Optional single-sentence "what changed this
 *                   quarter" band rendered between description and
 *                   north-star. Schema:
 *                   { gainer?: {label, deltaText}, decliner?:
 *                     {label, deltaText}, headline?: string }.
 *                   When `headline` is passed it's rendered
 *                   verbatim; otherwise we synthesise a sentence
 *                   from gainer + decliner. Skip silently if none
 *                   are passed.
 *   - pinnable:     boolean (default true) — render a "Pin to
 *                   Quarterly Deck" button that snapshots the
 *                   current title/description/theme/stats into
 *                   `quarterly_deck_pins`.
 *   - testId:       data-testid suffix for the root (default 'page-hero')
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Pin, PinOff, Loader2, ArrowUp, ArrowDown, Minus, Trophy, HelpCircle, Sparkles } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, Tooltip as RTooltip } from 'recharts';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import { isWarmupFrame } from '../../lib/pageCache';
import logger from '../../lib/logger';
import { StatWhyPanel } from './StatWhyPanel';
import { useRegisterPageTitle } from '../../context/PageTitleContext';

export function PageHero({
  title,
  titleHint,
  description,
  theme,
  icon: Icon,
  actions,
  stats,
  northStar,
  narrative,
  pinnable = true,
  pinSnapshot,
  testId = 'page-hero',
  children,
}) {
  const location = useLocation();
  const [pinning, setPinning] = useState(false);
  const [pinned, setPinned] = useState(false);
  // Publish the live title so the Header breadcrumb mirrors this hero.
  useRegisterPageTitle(title);
  const [whyOpen, setWhyOpen] = useState(false);
  const [activeWhy, setActiveWhy] = useState(null);
  const openWhy = (whyPanel) => {
    if (!whyPanel) return;
    setActiveWhy(whyPanel);
    setWhyOpen(true);
  };
  // Light/dark hero detection. The new `getLightSectionTheme()`
  // helper sets `textBase` to a slate-* class; legacy themes leave
  // it undefined (defaulting to text-white). When the hero is light
  // we need to swap every hardcoded white token below (stat values,
  // delta chips, north-star chrome, pin button) for slate
  // equivalents so the data doesn't render white-on-white.
  const isLight = String(theme?.textBase || '').includes('slate');
  const lt = isLight
    ? {
        // Stats grid
        statTileBorder: 'border-slate-200',
        statTileHover: 'hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300',
        statValue: 'text-slate-900',
        deltaNeutral: 'text-slate-500',
        deltaGood: 'text-emerald-700',
        deltaBad: 'text-rose-700',
        // North-star card
        nsCard: 'bg-white border border-slate-200 shadow-sm',
        nsCardHover: 'hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300',
        nsValue: 'text-slate-900',
        nsDivider: 'border-slate-200',
        peerTrack: 'bg-slate-200',
        peerFill: 'bg-[#44b6d2]',
        // Pin button
        pinIdle: 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50',
        pinActive: 'bg-slate-100 border-slate-300 text-slate-700 cursor-default',
      }
    : {
        statTileBorder: 'border-white/15',
        statTileHover: 'hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/40',
        statValue: 'text-white',
        deltaNeutral: 'text-white/80',
        deltaGood: 'text-emerald-200',
        deltaBad: 'text-rose-200',
        nsCard: 'bg-white/10 backdrop-blur-sm border border-white/20 shadow-inner',
        nsCardHover: 'hover:bg-white/15 transition-colors focus:outline-none focus:ring-2 focus:ring-white/40',
        nsValue: 'text-white',
        nsDivider: 'border-white/15',
        peerTrack: 'bg-white/15',
        peerFill: 'bg-white/85',
        pinIdle: 'bg-white/10 border-white/20 text-white hover:bg-white/20',
        pinActive: 'bg-white/30 border-white/40 text-white cursor-default',
      };
  // Page-view beacon — see PageHeroCompact for rationale.
  useEffect(() => {
    if (!title || isWarmupFrame()) return;
    api
      .post('/analytics/page-view/track', {
        path: location.pathname || '/',
        title: String(title),
        theme: theme?.name || null,
      })
      .catch((err) => logger.debug?.('page-view track failed', err));
  }, [title, theme?.name, location.pathname]);

  const handlePin = async () => {
    if (pinning || pinned) return;
    setPinning(true);
    // Snapshot current stats into a string-friendly shape so the
    // backend can render them into the appendix slide verbatim.
    // Capture delta info too so the home-page "What Changed?"
    // widget can rank movers without re-fetching dashboards.
    // When `stats` is custom JSX (e.g. richHeroStats on Maturity
    // Health), the page should pass `pinSnapshot` — an array of
    // {label,value,...} objects representing the same data — so
    // the pin still captures real numbers.
    const sourceArr = Array.isArray(stats)
      ? stats
      : (Array.isArray(pinSnapshot) ? pinSnapshot : []);
    const statsArr = sourceArr.map((s) => ({
      label: String(s.label || ''),
      value: String(s.value ?? ''),
      hint: s.hint ? String(s.hint) : undefined,
      delta: typeof s.delta === 'number' ? s.delta : undefined,
      delta_text: s.deltaText ? String(s.deltaText) : undefined,
      delta_label: s.deltaLabel ? String(s.deltaLabel) : undefined,
      delta_inverted: s.deltaInverted ? true : undefined,
    }));
    try {
      await api.post('/quarterly-deck/pins', {
        path: location.pathname || '/',
        title: String(title || ''),
        description: description ? String(description) : null,
        theme: theme?.name || null,
        stats: statsArr,
      });
      setPinned(true);
      toast.success('Pinned to Quarterly Deck');
    } catch (err) {
      logger.error?.('pin to quarterly deck failed', err);
      toast.error('Could not pin — try again');
    } finally {
      setPinning(false);
    }
  };

  const renderStats = () => {
    if (!stats) return null;
    if (!Array.isArray(stats)) return stats;
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mt-6">
        {stats.map((s, i) => {
          // Optional delta chip — pass `delta` (number) and an
          // optional `deltaLabel` (default "vs last quarter").
          // Direction is derived from sign; pass `deltaInverted`
          // for stats where down is good (e.g. attrition).
          const hasDelta = s.delta !== undefined && s.delta !== null && !Number.isNaN(Number(s.delta));
          let DeltaIcon = Minus;
          let deltaTone = lt.deltaNeutral;
          if (hasDelta) {
            const n = Number(s.delta);
            if (n > 0) {
              DeltaIcon = ArrowUp;
              deltaTone = s.deltaInverted ? lt.deltaBad : lt.deltaGood;
            } else if (n < 0) {
              DeltaIcon = ArrowDown;
              deltaTone = s.deltaInverted ? lt.deltaGood : lt.deltaBad;
            }
          }
          // Optional sparkline — pass `sparkline` as either a
          // numeric array or an array of {label, value}. The
          // sparkline area is hidden by default and revealed on
          // hover, so the resting tile keeps its clean readout.
          // Need at least 2 points to be meaningful.
          const sparklineRaw = Array.isArray(s.sparkline) ? s.sparkline : null;
          const sparklineData = sparklineRaw && sparklineRaw.length >= 2
            ? sparklineRaw.map((pt, idx) => (
                typeof pt === 'object' && pt !== null
                  ? { label: pt.label || `t${idx}`, value: Number(pt.value) || 0 }
                  : { label: `t${idx}`, value: Number(pt) || 0 }
              ))
            : null;
          return (
            <div
              key={s.label || i}
              role={s.whyPanel ? 'button' : undefined}
              tabIndex={s.whyPanel ? 0 : undefined}
              onClick={s.whyPanel ? () => openWhy(s.whyPanel) : undefined}
              onKeyDown={s.whyPanel ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openWhy(s.whyPanel);
                }
              } : undefined}
              className={cn(
                'group relative rounded-lg p-3 md:p-4 border backdrop-blur-sm transition-colors overflow-hidden',
                lt.statTileBorder,
                theme.statBg,
                s.whyPanel
                  ? `cursor-pointer ${lt.statTileHover}`
                  : (sparklineData ? 'cursor-help' : ''),
              )}
              data-testid={s.testId || `${testId}-stat-${i}`}
              aria-label={s.whyPanel ? `${s.label} — open explanation` : undefined}
            >
              {s.whyPanel && (
                <HelpCircle
                  className={cn(
                    'absolute top-2 right-2 h-3.5 w-3.5 opacity-50 group-hover:opacity-100 transition-opacity',
                    theme.textLighter,
                  )}
                  aria-hidden="true"
                  data-testid={`${s.testId || `${testId}-stat-${i}`}-why-icon`}
                />
              )}
              <p className={cn('text-[11px] md:text-xs uppercase tracking-wide font-medium', theme.textLighter)}>
                {s.label}
              </p>
              <p className={cn('text-2xl md:text-3xl font-bold mt-1 leading-tight', lt.statValue)}>
                {s.value}
              </p>
              {hasDelta && (() => {
                const dText = s.deltaText || (Number(s.delta) > 0 ? '+' : '') + s.delta;
                const dLabel = s.deltaLabel || 'vs last quarter';
                // Skip the trailing label when deltaText already
                // ends with " vs ..." so we don't render
                // "+4.6% vs last quarter vs last quarter".
                const skipLabel = / vs /i.test(String(s.deltaText || ''));
                return (
                  <div
                    className={cn('mt-1 inline-flex items-center gap-1 text-[11px] font-semibold', deltaTone)}
                    data-testid={`${s.testId || `${testId}-stat-${i}`}-delta`}
                    title={dLabel}
                  >
                    <DeltaIcon className="h-3 w-3" />
                    <span>{dText}</span>
                    {!skipLabel && (
                      <span className={cn('font-normal', theme.textLighter)}>
                        {dLabel}
                      </span>
                    )}
                  </div>
                );
              })()}
              {s.hint && !hasDelta && (
                <p className={cn('text-[11px] mt-1', theme.textLighter)}>{s.hint}</p>
              )}
              {s.hint && hasDelta && (
                <p className={cn('text-[11px] mt-0.5', theme.textLighter)}>{s.hint}</p>
              )}
              {sparklineData && (
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-12 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  data-testid={`${s.testId || `${testId}-stat-${i}`}-sparkline`}
                  aria-hidden="true"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={sparklineData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id={`spark-${testId}-${i}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={isLight ? '#44b6d2' : '#ffffff'} stopOpacity={isLight ? 0.25 : 0.55} />
                          <stop offset="100%" stopColor={isLight ? '#44b6d2' : '#ffffff'} stopOpacity={isLight ? 0.02 : 0.05} />
                        </linearGradient>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke={isLight ? '#44b6d2' : '#ffffff'}
                        strokeWidth={1.5}
                        fill={`url(#spark-${testId}-${i})`}
                        isAnimationActive={false}
                      />
                      <RTooltip
                        cursor={false}
                        contentStyle={{
                          background: 'rgba(15,23,42,0.92)',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 11,
                          color: '#fff',
                          padding: '4px 8px',
                        }}
                        labelStyle={{ color: 'rgba(255,255,255,0.65)' }}
                        formatter={(v) => [s.sparklineFormatter ? s.sparklineFormatter(v) : v, s.label]}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
              {sparklineData && (
                <span
                  className={cn(
                    'absolute top-2 right-2 text-[9px] uppercase tracking-wider opacity-60 group-hover:opacity-0 transition-opacity',
                    theme.textLighter,
                  )}
                  aria-hidden="true"
                >
                  6Q
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'relative w-full shadow-md overflow-hidden',
        theme.textBase || 'text-white',
        theme.heroGradient,
      )}
      data-testid={testId}
    >
      <div className="px-4 md:px-8 lg:px-12 py-6 md:py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {Icon && (
              <Icon
                className={cn('h-7 w-7 md:h-8 md:w-8 shrink-0', theme.titleClass)}
                data-testid={`${testId}-icon`}
              />
            )}
            <h1
              className={cn(
                'text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight',
                theme.titleClass,
              )}
              data-testid={`${testId}-title`}
            >
              {title}
            </h1>
            {titleHint && (
              <span
                className={cn(
                  'shrink-0 text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 self-center',
                  isLight
                    ? 'bg-slate-100 text-slate-500 border border-slate-200'
                    : 'bg-white/15 text-white/80 border border-white/20',
                )}
                title={`Internal engine: ${titleHint}`}
                data-testid={`${testId}-title-hint`}
              >
                {titleHint}
              </span>
            )}
          </div>
          {(actions || pinnable) && (
            <div
              className="flex flex-wrap items-center gap-2 md:gap-3 shrink-0"
              data-testid={`${testId}-actions`}
            >
              {pinnable && (
                <button
                  type="button"
                  onClick={handlePin}
                  disabled={pinning || pinned}
                  title={pinned ? 'Pinned to Quarterly Deck' : 'Pin to Quarterly Deck'}
                  aria-label={pinned ? 'Pinned to Quarterly Deck' : 'Pin to Quarterly Deck'}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all',
                    pinned
                      ? lt.pinActive
                      : lt.pinIdle,
                  )}
                  data-testid={`${testId}-pin-btn`}
                >
                  {pinning ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : pinned ? (
                    <PinOff className="h-3.5 w-3.5" />
                  ) : (
                    <Pin className="h-3.5 w-3.5" />
                  )}
                  {pinned ? 'Pinned' : 'Pin'}
                </button>
              )}
              {actions}
            </div>
          )}
        </div>
        {description && (
          <p
            className={cn(
              'mt-3 text-sm md:text-base w-full max-w-none leading-relaxed',
              theme.textLighter,
            )}
            data-testid={`${testId}-description`}
          >
            {description}
          </p>
        )}
        {narrative && (() => {
          const n = narrative;
          // Compose a sentence from gainer/decliner if no headline
          // was passed verbatim. Both fragments are optional —
          // skip the band entirely when nothing surfaceable exists.
          let sentence = (n.headline || '').trim();
          if (!sentence) {
            const parts = [];
            if (n.gainer && (n.gainer.label || n.gainer.deltaText)) {
              parts.push(
                `${n.gainer.label || 'Top mover'} ${n.gainer.deltaText || ''}`.trim()
                + ' · biggest gain'
              );
            }
            if (n.decliner && (n.decliner.label || n.decliner.deltaText)) {
              parts.push(
                `${n.decliner.label || 'Top decliner'} ${n.decliner.deltaText || ''}`.trim()
                + ' · attention needed'
              );
            }
            sentence = parts.join(' · ');
          }
          if (!sentence) return null;
          return (
            <div
              className={cn(
                'mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium',
                isLight
                  ? 'bg-white border-slate-200 text-slate-700 shadow-sm'
                  : 'bg-white/10 border-white/20 backdrop-blur-sm text-white',
              )}
              data-testid={`${testId}-narrative`}
            >
              <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="leading-tight" data-testid={`${testId}-narrative-text`}>
                {sentence}
              </span>
            </div>
          );
        })()}
        {northStar && (() => {
          const ns = northStar;
          const nd = typeof ns.delta === 'number' ? ns.delta : null;
          const nsHasDelta = nd !== null;
          const nsGood = nsHasDelta && (
            (nd > 0 && !ns.deltaInverted) ||
            (nd < 0 && ns.deltaInverted) ||
            (nd === 0)
          );
          const NsArrow = !nsHasDelta || nd === 0 ? Minus : (nd > 0 ? ArrowUp : ArrowDown);
          const nsTone = !nsHasDelta || nd === 0
            ? lt.deltaNeutral
            : (nsGood ? lt.deltaGood : lt.deltaBad);
          const nsId = ns.testId || `${testId}-northstar`;
          return (
            <div
              className={cn(
                'mt-6 relative rounded-2xl overflow-hidden',
                lt.nsCard,
                ns.whyPanel ? `cursor-pointer ${lt.nsCardHover}` : '',
              )}
              role={ns.whyPanel ? 'button' : undefined}
              tabIndex={ns.whyPanel ? 0 : undefined}
              onClick={ns.whyPanel ? () => openWhy(ns.whyPanel) : undefined}
              onKeyDown={ns.whyPanel ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openWhy(ns.whyPanel);
                }
              } : undefined}
              aria-label={ns.whyPanel ? `${ns.label} — open explanation` : undefined}
              data-testid={nsId}
            >
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-0">
                {/* Left half — giant value + delta */}
                <div className="px-6 md:px-8 py-6 md:py-7">
                  <div className="flex items-center gap-2">
                    <Trophy className={cn('h-3.5 w-3.5', theme.textLighter)} />
                    <span className={cn('text-[10px] uppercase tracking-widest font-semibold', theme.textLighter)}>
                      {ns.label || 'North Star'}
                    </span>
                    {ns.whyPanel && (
                      <HelpCircle
                        className={cn('h-3.5 w-3.5 opacity-60', theme.textLighter)}
                        aria-hidden="true"
                        data-testid={`${nsId}-why-icon`}
                      />
                    )}
                  </div>
                  <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                    <span
                      className={cn(
                        'font-extrabold leading-none tracking-tight',
                        'text-5xl md:text-6xl lg:text-7xl',
                        ns.valueTone || lt.nsValue,
                      )}
                      data-testid={`${nsId}-value`}
                    >
                      {ns.value ?? '—'}
                    </span>
                    {ns.valueSuffix && (
                      <span className={cn('text-xl md:text-2xl font-semibold', theme.textLighter)}>
                        {ns.valueSuffix}
                      </span>
                    )}
                  </div>
                  {ns.description && (
                    <p className={cn('mt-2 text-sm', theme.textLighter)} data-testid={`${nsId}-description`}>
                      {ns.description}
                    </p>
                  )}
                  {nsHasDelta && (() => {
                    const dText = ns.deltaText || (nd > 0 ? '+' : '') + nd;
                    const skipLabel = / vs /i.test(String(ns.deltaText || ''));
                    return (
                      <div
                        className={cn('mt-3 inline-flex items-center gap-1.5 text-sm font-semibold', nsTone)}
                        data-testid={`${nsId}-delta`}
                      >
                        <NsArrow className="h-3.5 w-3.5" />
                        <span>{dText}</span>
                        {!skipLabel && (
                          <span className={cn('font-normal', theme.textLighter)}>
                            {ns.deltaLabel || 'vs last quarter'}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Right half — percentile band + peer comparison +
                    sparkline.  All optional; when none are passed
                    the right column collapses to maintain the same
                    aesthetic on simpler pages. */}
                <div className={cn('px-6 md:px-8 py-6 md:py-7 border-t md:border-t-0 md:border-l flex flex-col justify-center gap-3', lt.nsDivider)}>
                  {typeof ns.percentile === 'number' && (
                    <div data-testid={`${nsId}-percentile`}>
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className={theme.textLighter}>
                          {ns.percentileLabel || 'Peer percentile'}
                        </span>
                        <span className="font-bold tabular-nums">
                          {Math.round(ns.percentile)}
                          <span className={cn('font-normal', theme.textLighter)}>th</span>
                        </span>
                      </div>
                      <div className={cn('h-2 rounded-full overflow-hidden', lt.peerTrack)}>
                        <div
                          className={cn('h-full rounded-full transition-all duration-500', lt.peerFill)}
                          style={{ width: `${Math.min(100, Math.max(0, ns.percentile))}%` }}
                          aria-label={`${Math.round(ns.percentile)}th percentile`}
                        />
                      </div>
                    </div>
                  )}
                  {(ns.peerLabel || ns.peerValue) && (
                    <div className="flex items-center justify-between text-[11px]" data-testid={`${nsId}-peer`}>
                      <span className={theme.textLighter}>
                        {ns.peerLabel || 'Peer median'}
                      </span>
                      <span className="font-semibold tabular-nums">
                        {ns.peerValue ?? '—'}
                      </span>
                    </div>
                  )}
                  {Array.isArray(ns.sparklineData) && ns.sparklineData.length > 1 && (
                    <div className="h-14 -mx-2" data-testid={`${nsId}-sparkline`} aria-hidden="true">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={ns.sparklineData}
                          margin={{ top: 2, right: 0, left: 0, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient id={`ns-spark-${testId}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#ffffff" stopOpacity={0.6} />
                              <stop offset="100%" stopColor="#ffffff" stopOpacity={0.06} />
                            </linearGradient>
                          </defs>
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#ffffff"
                            strokeWidth={2}
                            fill={`url(#ns-spark-${testId})`}
                            isAnimationActive={false}
                          />
                          <RTooltip
                            cursor={false}
                            contentStyle={{
                              background: 'rgba(15,23,42,0.92)',
                              border: 'none',
                              borderRadius: 6,
                              fontSize: 11,
                              color: '#fff',
                              padding: '4px 8px',
                            }}
                            labelStyle={{ color: 'rgba(255,255,255,0.65)' }}
                            formatter={(v) => [
                              ns.sparklineFormatter ? ns.sparklineFormatter(v) : v,
                              ns.label,
                            ]}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
        {renderStats()}
        {children}
      </div>
      <StatWhyPanel
        open={whyOpen}
        onClose={() => setWhyOpen(false)}
        why={activeWhy}
      />
    </div>
  );
}

export default PageHero;

